/** Calendar dates are local civil dates, never UTC timestamps. */
export function localDateISO(date = new Date()): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
export function displayDate(value: string): string {
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return iso ? `${iso[3]}/${iso[2]}/${iso[1]}` : value;
}
export function formatDateEntry(value: string): string {
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return displayDate(value);
  const parts = value.split("/");
  if (parts.length >= 3) {
    const [d, m, y] = parts.map((part) => part.replace(/\D/g, ""));
    const formatted = `${d.slice(0, 2)}/${m.slice(0, 2)}/${y.slice(0, 4)}`;
    const iso = calendarDateISO(formatted);
    return iso ? displayDate(iso) : formatted;
  }
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits + (value.endsWith("/") && digits.length ? "/" : "");
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}` + (value.endsWith("/") && digits.length === 4 ? "/" : "");
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}
export function calendarDateISO(value: string): string | null {
  const match = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(displayDate(value));
  if (!match) return null;
  const [, day, month, year] = match;
  const d = Number(day), m = Number(month), y = Number(year);
  if (y < 1900 || y > 2200 || m < 1 || m > 12 || d < 1) return null;
  const date = new Date(y, m - 1, d, 12);
  return date.getFullYear() === y && date.getMonth() === m - 1 && date.getDate() === d
    ? `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}` : null;
}
export function formatTimeEntry(value: string): string {
  if (!value.trim()) return "";
  const twelveHour = /^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i.exec(value.trim());
  if (twelveHour) {
    const hour = Number(twelveHour[1]), minute = Number(twelveHour[2] ?? "0");
    if (hour >= 1 && hour <= 12 && minute < 60) {
      return `${String(hour % 12 + (twelveHour[3].toLowerCase() === "pm" ? 12 : 0)).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
    }
  }
  if (value.includes(":")) {
    const [hour, minute = ""] = value.split(":");
    const h = hour.replace(/\D/g, "").slice(0, 2);
    return `${h.length === 1 && minute.length >= 2 ? h.padStart(2, "0") : h}:${minute.replace(/\D/g, "").slice(0, 2)}`;
  }
  const digits = value.replace(/\D/g, "").slice(0, 4);
  if (digits.length === 3 && Number(digits.slice(0, 2)) > 23) return `0${digits[0]}:${digits.slice(1)}`;
  return digits.length <= 2 ? digits : `${digits.slice(0, 2)}:${digits.slice(2)}`;
}
export function validTime(value: string): boolean {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(value);
}
export function dateFromISO(value: string): Date | undefined {
  const iso = calendarDateISO(value);
  if (!iso) return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d, 12);
}
